import { Directional } from '../../../../components/Directional/Directional';
import { Note } from '../../../../components/Note/Note';
import {
  AdSyncNotPosted,
  AdSyncClosed,
  AdSyncPosted,
  AdSyncPrefix,
  AdSyncSource,
  AdSyncSuspended,
  AdSyncUpdated
} from './components.tsx';

# Display job ads

After your software has [synced a position],
the SEEK API can notify you when Ad Sync posts or updates a corresponding job ad.
This is driven by asynchronous [lifecycle events](#lifecycle-events) as SEEK may not process the position sync in real time.

When your software receives a Job Posting event and retrieves the corresponding `PostedPositionProfile` object from our [GraphQL endpoint],
correlate it to a job requisition in your software via [identifiers & references],
then display it in your job ad list view.
This improves hirer understanding and reduces the likelihood that they will inadvertently post a duplicate ad via [classic] or [Enhanced Job Posting].

An open position is not guaranteed to be automatically posted by Ad Sync.
Hirers can specify [rules] to exclude certain positions,
and data [duplication] or quality issues may prevent Ad Sync from advertising the position.

```mermaid
sequenceDiagram
  participant P as Partner
  participant A as auth.seek.com
  participant G as graphql.seek.com

  G-->>P: Webhook with Job Posting event & object identifier
  P->>A: Send client credentials
  A-->>P: Issue partner token
  P->>G: Request PostedPositionProfile via GraphQL
  G-->>P: Return PostedPositionProfile
  P->>P: Correlate to job requisition
  P->>P: Display in job ad list view
  P->>P: Pre-fill edit flow from PostedPositionProfile
```

When your software receives a [`PositionProfilePosted`] or [`PositionProfileUpdated`] event,
use the `positionProfileId` from the event to [query the job ad].
The response includes SEEK [job category] and [location] identifiers resolved from SEEK’s taxonomies,
alongside human-readable names you can show in your UI.
This is the source of truth for what is live on SEEK,
including values inferred by Ad Sync when the position was posted.
See [`positionProfile`] for a full query and response example.

Use the query response to populate your job ad list and detail views only.
When a hirer edits a job requisition, propagate the change with the same [`syncPositions` mutation] you use to create and update positions.
Updating a live job ad outside Ad Sync breaks automatic sync from the job requisition to the job ad.

Ad Sync may infer a number of fields on the job ad that your software did not supply in the original sync.
See [Persisting inferred values] on the sync positions page for the fields this applies to, and guidance on retrieving and persisting these values back into your software.

## Posting sources

Your software must differentiate job ads that were automatically posted by Ad Sync from those that a hirer manually posted.

It is recommended to drive this differentiation off of the [`seekPostingSourceCode`] available when querying the state of a `PostedPositionProfile`, otherwise you can use a posting source value that your software stores at the time of posting.

Include an `(Ad Sync)` qualifier in the channel or source field of your software.
Your software may also support advanced sorting and filtering by this field.

<AdSyncSource />

If this is not feasible, your software may add an `(Ad Sync)` prefix to the title field of the job ad.

<AdSyncPrefix />

When querying the state of a `PostedPositionProfile`,
your software will receive different `seekCreatedBySelfIndicator` and `seekPostingSourceCode` values depending on the posting source.

```jsonc label="Ad Sync via SEEK API"
// Treat as automated posting
{
  "seekCreatedBySelfIndicator": true,
  "seekPostingSourceCode": "AdSync"
}
```

```jsonc label="Ad Sync via feeds & web scraping"
// Treat as automated posting
{
  "seekCreatedBySelfIndicator": false,
  "seekPostingSourceCode": "AdSync"
}
```

```jsonc label="Job Posting via SEEK API"
// Treat as manual posting
{
  "seekCreatedBySelfIndicator": true,
  "seekPostingSourceCode": "CurrentPartner"
}
```

```jsonc label="SEEK Employer website"
// Treat as manual posting
{
  "seekCreatedBySelfIndicator": false,
  "seekPostingSourceCode": "Seek"
}
```

## Lifecycle events

Subscribe to `PositionNotPosted`, `PositionProfilePosted`, `PositionProfileUpdated`, and `PositionProfileClosed` events to keep your software in sync.
See our [event] documentation for more context.

```mermaid
flowchart TD

Start@{ shape: circle, label: "Post" }
PositionNotPosted@{ shape: braces, label: "PositionNotPosted" }
PositionProfilePosted@{ shape: braces, label: "PositionProfilePosted" }
PositionProfileUpdated@{ shape: braces, label: "PositionProfileUpdated" }
PositionProfileUpdated2@{ shape: braces, label: "PositionProfileUpdated" }
PositionProfileClosed@{ shape: braces, label: "PositionProfileClosed" }

Start --> PostDecision{pass sync check?}

PostDecision --> |no| PositionNotPosted -.-> NotPosted[Not Posted]
PostDecision ---> |yes| PositionProfilePosted -.-> Active[Active]

NotPosted --> |update| PostDecision
NotPosted --> |expire| PositionProfileClosed

Active --> |update| PositionProfileUpdated -.-> Active
Active ---> |fail async check| PositionProfileUpdated2 -.-> Suspended[Suspended]
Active ---> |expire| PositionProfileClosed

Suspended ---> |reactivate| PositionProfileUpdated
Suspended ----> |expire| PositionProfileClosed

PositionProfileClosed -.-> Closed[Closed]
```

### PositionNotPosted

A [PositionNotPosted] event will be emitted whenever a position cannot be posted on SEEK, whether due to insufficient information in the [`syncPositions` mutation], an issue with the hirer’s SEEK account or Ad Sync configuration, an intentional exclusion via the hirer’s Ad Sync rules, or following a job ad being [updated outside Ad Sync], which prevents future Ad Sync updates from propagating to the job ad.

Your software may surface a SEEK provided message explaining why the position wasn’t posted and guidance on how to resolve it.

<AdSyncNotPosted />

To determine why a position was not posted, use the `reasonId` included in the [PositionNotPosted] event to query [positionNotPostedReason](#positionnotpostedreason). This will return a detailed explanation and suggested actions for the specific posting failure.

To retrieve a list of historical reasons associated with a position, use the `seekPartnerPositionId` included in the [PositionNotPosted] event to query [positionNotPostedReasons](#positionnotpostedreasons).

To test how your software handles [PositionNotPosted] events, include a supported key in `positionTitle` when calling the [`syncPositions` mutation].

For example, setting `positionTitle` to include `EXPIRED_AD` will prevent the job ad from being posted on SEEK and emit a [PositionNotPosted] event.

```json label="Input snippet"
{
  "positionTitle": "Senior Business Analyst EXPIRED_AD"
}
```

Some example keys you can use to test different scenarios:

- `CATEGORY_INFERENCE_FAILED`
- `EXPIRED_AD`
- `LOCATION_INFERENCE_FAILED`
- `SALARY_INFERENCE_FAILED`

<Note /> These keys are for **testing purposes only**. The content and mapping
of these codes is subject to change and should not be relied upon.

#### positionNotPostedReason

The [`positionNotPostedReason` query] accepts a `reasonId`, included in the [PositionNotPosted] event, and returns the reason a position was not posted to SEEK, along with any suggested remediation actions.

Query the `status`, `message`, and `callToAction` fields to clearly communicate the issue and recommended next steps to the hirer.
You may also query `displaySeverityCode` to better tailor how the message is presented - for example, by styling errors more prominently than informational notices.

```graphql
query ($input: PositionNotPostedReasonInput!) {
  positionNotPostedReason(input: $input) {
    status
    message
    displaySeverityCode
    errors {
      field
      value
      message
    }
    callToAction
    timestamp
  }
}
```

```json
{
  "input": {
    "reasonId": "globalPublicTest:position:notPosted:1111114bZ6BZRUqUqZeo"
  }
}
```

```json
{
  "positionNotPostedReason": {
    "status": "Couldn't post",
    "message": "SEEK couldn't post this ad as there may be an issue with your account. Contact your SEEK account manager to resolve this.",
    "displaySeverityCode": "error",
    "errors": [
      {
        "field": "field-0",
        "value": "value-0",
        "message": "Example message for ACCOUNT_CREDIT_HOLD"
      }
    ],
    "callToAction": "ContactSeek",
    "timestamp": "2024-01-01T00:00:00.000Z"
  }
}
```

### positionNotPostedReasons

The [`positionNotPostedReasons` query] accepts a `seekPartnerPositionId`, included in the [PositionNotPosted] event, along with the `seekAdSyncId` and optional `seekSubOrgReference` used when posting the position with the [`syncPositions` mutation].

It returns the 25 most recent historical reasons why a position was not posted to SEEK, along with any suggested remediation actions.

```graphql
query ($input: PositionNotPostedReasonsInput!) {
  positionNotPostedReasons(input: $input) {
    status
    message
    displaySeverityCode
    errors {
      field
      value
      message
    }
    callToAction
    timestamp
  }
}
```

```json
{
  "input": {
    "seekPartnerPositionId": "REQ-123",
    "seekAdSyncId": "seek-adSync-PlayGroundId",
    "seekSubOrgReference": "acme-melbourne"
  }
}
```

```json
{
  "positionNotPostedReasons": [
    {
      "status": "Couldn't edit",
      "message": "This ad wasn't updated because it was edited via a different method. To update it, edit the ad via the same method.",
      "displaySeverityCode": "warning",
      "errors": [
        {
          "field": "field-11",
          "value": "value-11",
          "message": "Example message for EDIT_BLOCKED"
        }
      ],
      "callToAction": null,
      "timestamp": "2024-01-12T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad as something went wrong. Try again, or contact SEEK if the problem continues.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-10",
          "value": "value-10",
          "message": "Example message for OTHER"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-11T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because salary details were missing and couldn't be worked out from the job details. Add salary information to the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-9",
          "value": "value-9",
          "message": "Example message for SALARY_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-10T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because we couldn't find a matching job category. Update the job title or description in the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-8",
          "value": "value-8",
          "message": "Example message for CATEGORY_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-09T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because the job location isn't supported by SEEK. Update the location in the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-7",
          "value": "value-7",
          "message": "Example message for LOCATION_NON_SEEK_MARKET"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-08T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn’t post this ad because we couldn’t find a valid location in your job details. Update the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-6",
          "value": "value-6",
          "message": "Example message for LOCATION_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-07T00:00:00.000Z"
    },
    {
      "status": "Not posted",
      "message": "This ad wasn't posted on SEEK because it was excluded based on your business Ad Sync rules. To post it, contact SEEK to update your rules.",
      "displaySeverityCode": "info",
      "errors": [
        {
          "field": "field-5",
          "value": "value-5",
          "message": "Example message for EXCLUSION_RULE_APPLIED"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-06T00:00:00.000Z"
    },
    {
      "status": "Not posted",
      "message": "This ad wasn't posted on SEEK because you included an #s-dni tag in the job description. To post it, edit the requisition to remove the tag.",
      "displaySeverityCode": "info",
      "errors": [
        {
          "field": "field-4",
          "value": "value-4",
          "message": "Example message for DNI_TAG_DETECTED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-05T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because it's already expired.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-3",
          "value": "value-3",
          "message": "Example message for EXPIRED_AD"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-04T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "We couldn't post this ad because it's not linked to a SEEK account. Contact your SEEK account manager to update your preferences.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-2",
          "value": "value-2",
          "message": "Example message for ACCOUNT_NOT_FOUND"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-03T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because it appears to be a duplicate.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-1",
          "value": "value-1",
          "message": "Example message for DUPLICATE_AD_DETECTED"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-02T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad as there may be an issue with your account. Contact your SEEK account manager to resolve this.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-0",
          "value": "value-0",
          "message": "Example message for ACCOUNT_CREDIT_HOLD"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-01T00:00:00.000Z"
    }
  ]
}
```

<Note /> Requests will be routed to the Playground environment if `seekAdSyncId`
is `seek-adSync-PlayGroundId`

### PositionProfilePosted

Once a job ad has been posted,
provide options to view the job ad on SEEK,
and to manage the job ad via classic or Enhanced Job Posting.
The latter allows hirers to further tailor the content of job ads posted via Ad Sync.

When a hirer edits the job requisition for an Ad Sync job ad,
use your existing position edit flow and forward the full updated state via the [`syncPositions` mutation].
See [Persisting inferred values] for guidance on surfacing and reusing values Ad Sync inferred when the job ad was first posted.

<AdSyncPosted />

### PositionProfileUpdated

Hirers can use the SEEK Employer website or a classic or Enhanced Job Posting integration to edit job ads posted via Ad Sync.
Keep the details in your software in sync with these changes.

{/* prettier-ignore */}
<Directional /> A new `PostedPositionProfile.seekStatusCode` field is available for your software to select.
This will be `Active` while the job ad is listed on our employment marketplace.

<AdSyncUpdated />

{/* prettier-ignore */}
<Directional /> In rare cases, SEEK may flag a job ad after it has been posted, move it to a `Suspended` status, and emit another `PositionProfileUpdated` event.

This may be caused by duplicate job ads being posted under the same hirer account, or issues with the job ad content.

Your software may include a call to action for hirers to reactivate suspended job ads by contacting SEEK.

{/* prettier-ignore */}
<Directional /> Ad Sync may be extended in a later release to emit a [PositionNotPosted] event when an update to a position profile has failed.

<AdSyncSuspended />

### PositionProfileClosed

Expiration is a terminal state for the job ad.
Disable the action to view it on SEEK.

{/* prettier-ignore */}
<Directional /> Provide an option to re-post the job ad via Enhanced Job Posting, once available.

<AdSyncClosed />

[`PositionProfilePosted`]: #positionprofileposted
[`PositionProfileUpdated`]: #positionprofileupdated
[`positionProfile`]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile
[`seekPostingSourceCode`]: /schema/named-type/PostedPositionProfile#/field/seekPostingSourceCode
[`syncPositions` mutation]: /schema/mutation/syncPositions
[classic]: /use-cases/job-posting/classic
[duplication]: /use-cases/job-posting/job-ad-duplication
[Enhanced Job Posting]: /use-cases/job-posting/enhanced
[Persisting inferred values]: /use-cases/job-posting/ad-sync/sync-positions#persisting-inferred-values
[updated outside Ad Sync]: /use-cases/job-posting/ad-sync/sync-positions#updating-a-job
[job category]: /schema/named-type/PostedPositionProfile#/field/jobCategories
[location]: /use-cases/job-posting/ad-sync/sync-positions#positionlocation
[query the job ad]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile
[event]: /events
[GraphQL endpoint]: /graphql
[identifiers & references]: /use-cases/job-posting/identifiers-and-references#reference-fields
[synced a position]: /use-cases/job-posting/ad-sync/sync-positions
[rules]: /use-cases/job-posting/ad-sync#seek-posting-configuration
[PositionNotPosted]: /schema/webhooks/PositionNotPostedEvent
[`positionNotPostedReason` query]: /schema/query/positionNotPostedReason
[`positionNotPostedReasons` query]: /schema/query/positionNotPostedReasons
